import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import { InsiIdentityStatusIds, AttendingPhysicianStatusIds } from '@toktokdoctech/tokapi-client'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArticleRoundedIcon from '@mui/icons-material/ArticleRounded'
import EditRoundedIcon from '@mui/icons-material/EditRounded'
import { useCallback, useMemo } from 'react'
import { Tooltip } from '@mui/material'
import moment from 'moment'
import {
    TokTokDocDataTableHeader,
    TokTokDocDataTableHeaders,
    TokTokDocDataTableCell,
} from '../../Common/_props/TokTokDocDataTableProps'
import useStyles from '../../Common/_styles/dataTableStyles'
import {
    PatientDataTableHeaderName,
    PatientDataTableHeadersMap,
    PatientDataTableCellsMap,
    PatientsDataTableRow,
    UsePatientDataTableConfigProps,
} from './_props'
import PatientIdentityStatus from '../Form/PatientIdentity/PatientIdentityStatus'
import DataTableActions from '../../Common/dataTableActions/DataTableActions'
import useCurrentHcs from '../../../hooks/useCurrentHcs'
import { getAge } from '../../../utils/dates'
import { PatientIdentityStatusEnum } from '../Form/_props'
import DataTableIcon from '../../dataTableIcon/DataTableIcon'
import { WarningIcon } from '../../../assets/icons/icons'

const buildHeaders = (
    order: PatientDataTableHeaderName[],
    headersMap: Record<PatientDataTableHeaderName, TokTokDocDataTableHeader>,
): TokTokDocDataTableHeaders => {
    const res = order.map<TokTokDocDataTableHeader>((key) => headersMap[key])

    return res
}

const getOrder = (isAdvancedModeActivated: boolean): PatientDataTableHeaderName[] => {
    if (isAdvancedModeActivated) {
        return [
            'INS',
            'birthName',
            'firstFirstName',
            'firstNames',
            'birthDate',
            'sex',
            'requiredExpertInCharge',
            'actions',
        ]
    }
    return ['INS', 'birthName', 'firstFirstName', 'birthDate', 'sex', 'actions']
}

const transformPhysicianLabel = (attendingPhysicianStatus: AttendingPhysicianStatusIds) => {
    if (attendingPhysicianStatus === AttendingPhysicianStatusIds.Yes) return 'Oui'
    if (attendingPhysicianStatus === AttendingPhysicianStatusIds.No) return 'Aucun'
    if (attendingPhysicianStatus === AttendingPhysicianStatusIds.NotSpecified) return 'Non renseigné'
    throw new Error(`[Patient data table] - unrecognized attendingPhysicianStatus: ${attendingPhysicianStatus}`)
}

const transformInsiStatus = (insiStatus: InsiIdentityStatusIds): PatientIdentityStatusEnum =>
    insiStatus.toUpperCase() as PatientIdentityStatusEnum // TODO refacto this enum

export const usePatientDataTableConfig = ({
    sort,
    handleSort,
    isAdvancedModeActivated,
}: UsePatientDataTableConfigProps) => {
    const { arePatientsInmate } = useCurrentHcs()

    const classes = useStyles()
    const headersMap: PatientDataTableHeadersMap = useMemo(
        () => ({
            INS: {
                label: 'Statut INS',
                size: 'xs',
                orientation: 'center',
            },
            birthName: {
                label: 'Nom de naissance (Nom utilisé)',
                size: 'md',
                buttonProps: {
                    endIcon: sort === 'descending' ? <ArrowDownwardIcon /> : <ArrowUpwardIcon />,
                    disableElevation: true,
                    onClick: handleSort,
                },
            },
            firstFirstName: {
                label: '1er prénom de naissance (Prénom utilisé)',
                size: 'md',
                tooltip: `Cette colonne permet de visualiser le "1er prénom de naissance" ainsi que l'éventuel "prénom utilisé" du patient. Si cette information n'a pas été renseignée, seul le "1er prénom de naissance" s'affiche`,
            },
            firstNames: {
                size: 'md',
                tooltip: `Cette colonne permet de visualiser la liste des prénoms de naissance du patient. Si le patient n'a qu'un seul prénom de naissance, seul le "1er prénom de naissance" s'affiche`,
                label: 'Prénom(s) de naissance',
            },
            birthDate: {
                size: 'md',
                label: 'Date de naissance (Âge)',
            },
            sex: {
                size: 'xs',
                label: 'Sexe',
            },
            requiredExpertInCharge: {
                size: 'sm',
                label: 'Médecin',
            },
            actions: {
                label: 'Actions',
                size: 'sm',
                orientation: 'center',
                sticky: true,
                stickyAnchor: 'right',
            },
        }),
        [handleSort, sort],
    )

    const getRowsMap = useCallback(
        (patient: PatientsDataTableRow): PatientDataTableCellsMap => {
            const birthNameLabel = `${patient.birthName} ${patient.lastName ? `(${patient.lastName})` : ''}`
            const age = getAge(patient.birthDate)
            const ageLabel = `${age} ${age === 1 ? 'an' : 'ans'}`

            const getFirstFirstNameLabel = (firstName: string | undefined, firstFirstName: string | undefined) => {
                if (!firstFirstName || firstName === firstFirstName) return firstName
                return `${patient.firstFirstname} (${patient.firstName})`
            }

            return {
                INS: {
                    item: <PatientIdentityStatus status={transformInsiStatus(patient.insiStatus)} display="dot" />,
                },
                birthName: {
                    hasError: !arePatientsInmate && !patient.hasAddress,
                    item: (
                        <div>
                            {!arePatientsInmate && !patient.hasAddress ? (
                                <div className={classes.tableCellErrorNotification}>
                                    <DataTableIcon
                                        title="L'adresse du patient est requise"
                                        icon={<WarningIcon sx={{ width: '100%', height: '100%' }} />}
                                    />
                                    <Tooltip placement="bottom" title="L'adresse du patient est requise">
                                        <span>{birthNameLabel}</span>
                                    </Tooltip>
                                </div>
                            ) : (
                                <span>{birthNameLabel}</span>
                            )}
                        </div>
                    ),
                },
                firstFirstName: {
                    item: getFirstFirstNameLabel(patient.firstName, patient.firstFirstname),
                },
                firstNames: {
                    item: patient.firstNameList?.join(', '),
                },
                birthDate: {
                    item: `${moment(patient.birthDate).format('DD/MM/YY')} (${ageLabel})`,
                },
                sex: {
                    item: patient.sex?.toUpperCase(),
                },
                requiredExpertInCharge: {
                    item: transformPhysicianLabel(patient.attendingPhysicianStatus),
                },
                actions: {
                    item: (
                        <DataTableActions
                            actions={[
                                {
                                    to: `/patient/${patient?.id}`,
                                    icon: (
                                        <EditRoundedIcon
                                            data-cy="editIcon"
                                            fontSize="small"
                                            titleAccess="Modifier un patient"
                                        />
                                    ),
                                },
                                {
                                    to: `/patient-history/${patient?.id}`,
                                    icon: (
                                        <ArticleRoundedIcon fontSize="small" titleAccess="Historique des documents" />
                                    ),
                                    dataCy: 'patientHistory',
                                },
                            ]}
                        />
                    ),
                    orientation: 'center',
                    sticky: true,
                    stickyAnchor: 'right',
                },
            }
        },
        [arePatientsInmate],
    )

    const order = getOrder(isAdvancedModeActivated)

    const headers = buildHeaders(order, headersMap)

    const buildRowsMap = useCallback(
        (row: PatientsDataTableRow) => {
            const rowsMap = getRowsMap(row)

            return order.map<TokTokDocDataTableCell>((key) => rowsMap[key])
        },
        [getRowsMap, order],
    )

    return {
        headers,
        buildRowsMap,
    }
}
